import { ExecuteActionRequest, GameState, Notebook, RegisteredUser, User, Username } from "@shared/types";
import { BaseRouteService } from "./BaseRouteService";

/**
 * Service to communicate with game routes of the server application
 */
export class GameRouteService extends BaseRouteService {
    /**
     * Get a type of game state for the current player from the server application
     *
     * @returns A type of game state for the current player
     */
    public async getGameState(): Promise<GameState> {
        return this.getJsonApi<GameState>("game/state");
    }

    /**
     * Get the notebook content for the current player
     *
     * @returns - Notebook contnt
     */
    public async getNotebookContent(): Promise<Notebook> {
        return this.getJsonApi<Notebook>("game/notebook");
    }

    /**
     * Updates the notebook content for the current player
     *
     * @param updatedContent - The updated to content to save
     */
    public async updateNotebookContent(updatedContent: Notebook): Promise<void> {
        await this.postJsonApi<Notebook>("game/notebook/update", updatedContent);
    }

    public async getUser(user: User): Promise<User[]> {
        return this.postJsonApi<User[]>("game/login", user);
    }

    public async getExistingUsername(username: Username): Promise<Username[]> {
        return this.postJsonApi<Username[]>("game/register/username", username);
    }

    public async registerUser(user: User): Promise<RegisteredUser[]> {
        return this.postJsonApi<RegisteredUser[]>("game/register", user);
    }

    public async resetPlayerSession(): Promise<void> {
        await this.getJsonApi("game/reset");
    }

    public async getLoggedInUsername(userId: { userId: number }): Promise<Username[]> {
        return this.postJsonApi<Username[]>("game/username", userId);
    }

    /**
     * Execute an action for the current player on the server application
     *
     * @param actionAlias Alias of the action to execute
     * @param objectAliases Aliases of the game objects to execute the action on
     *
     * @returns A type of game state, or `undefined` if the action was unhandled. This can indicate the action requires a second game object to work.
     */
    public async executeAction(
        actionAlias: string,
        objectAliases?: string[]
    ): Promise<GameState | undefined> {
        try {
            return await this.postJsonApi<GameState, ExecuteActionRequest>("game/action", {
                action: actionAlias,
                objects: objectAliases,
            });
        }
        catch {
            return undefined;
        }
    }
}
